<template lang="pug">
aside-modal(:id="modalId", width="w-xtra-large", :loading="onLoading('save-modifier-sets')")
    span(slot="header-text") {{ modifierExists ? 'Update' : 'Create' }} Modifier Set
    div.padding(slot="body")
        form(id="modifierSetForm")
            fieldset
                div.md-form-group
                    input.md-input(
                        placeholder="e.g Addons, Add Topping",
                        type="text",
                        :value="modifierSet.name",
                        @input="updateModiferSet('name', $event)",
                        required
                    )
                    label Name

                p Selection Mode

                div.row
                    div.col-xs-6
                        div.md-form-group
                            input.md-input(
                                type="number",
                                required,
                                :value="modifierSet.min_apply_limit",
                                @input="updateModiferSet('min_apply_limit', $event)"
                            )
                            label Min Apply Limit

                    div.col-xs-6
                        div.md-form-group
                            input.md-input(
                                type="number",
                                required,
                                :value="modifierSet.max_apply_limit",
                                @input="updateModiferSet('max_apply_limit', $event)"
                            )
                            label Max Apply Limit

                div.md-form-group.p-t-0
                    label.multiselect-label Products
                    multiselect(
                        v-model="modifierSet.products",
                        label="name",
                        track-by="id",
                        placeholder="Type to search",
                        :options="products",
                        :multiple="true",
                        :loading="ajaxProductLoading",
                        :close-on-select="false",
                        :limit="20",
                        @search-change="loadProducts"
                    )

                p Modifiers

                draggable(
                    v-model="modifiers",
                    :options="{handle: '.drag-handler'}"
                )
                    div.modifiers-input-list(
                        v-for="(modifier, idx) in modifierSet.modifiers",
                        :key="idx"
                    )
                        div.row
                            div.col-xs-12.modifiers(
                                :class="{ 'p-l-lg': hasMultipleModifier }"
                            )
                                i.fa.fa-bars.drag-handler.text-muted.m-t-xs(
                                    v-show="hasMultipleModifier"
                                )

                                div.col-xs-1
                                    label.md-label Preselect
                                    p.p-t-md
                                        label.md-check
                                            input.has-value(
                                                type="checkbox",
                                                name="preselected",
                                                :disabled="disablePreselect",
                                                :checked="modifier.pre_selected == true",
                                                @change="updateModiferSet('modifiers', $event, 'pre_selected', idx)"
                                            )
                                            i.indigo

                                div.col-xs-4.p-l-md
                                    div.md-form-group
                                        select.md-input(
                                            required,
                                            :value="modifier.mode",
                                            @input="updateModiferSet('modifiers', $event, 'mode', idx)"
                                        )
                                            option(v-for="mode in modes" v-bind:value="mode.id")
                                                | {{ mode.name }}
                                        label Mode

                                div.col-xs-4
                                    div.md-form-group
                                        input.md-input(
                                            type="text",
                                            :value="modifier.name",
                                            required,
                                            placeholder="e.g Extra Cheese",
                                            @input="updateModiferSet('modifiers', $event, 'name', idx)"
                                        )
                                        label Name

                                div.col-xs-2
                                    div.md-form-group
                                        input.md-input(
                                            v-show="modifier.mode != 0",
                                            type="text",
                                            :value="modifier.amount",
                                            required,
                                            placeholder="0.00",
                                            data-parsley-type="number",
                                            @input="updateModiferSet('modifiers', $event, 'amount', idx)"
                                        )
                                        span.clear.p-t-sm.text-muted(v-show="modifier.mode == 0") N/A
                                        label Amount

                                div.col-xs-1(v-if="hasMultipleModifier")
                                    i.text-muted.material-icons.md-18.btn-del-list.pointer(
                                        @click="removeModifier(idx)"
                                    ) &#xe14c;

                div.row
                    div.col-xs-12
                        button.btn.btn-outline.btn-sm.rounded.b-success.text-success.pull-right(
                            type="button",
                            @click="addModifier"
                        )
                            i.material-icons.md-18 &#xe145;
                            | Add Modifiers

    button.btn.btn-fw.primary(
        slot="footer",
        @click="submit()",
        :disabled="onLoading('save-modifier-sets') || !canManageItems"
    ) 
        v-loading(loader="save-modifier-sets") Save
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Auth from '@js/plugins/auth';
import draggable from 'vuedraggable';

export default {
    components: {
        draggable,
    },

    data() {
        return {
            ajaxProductLoading: false,
            apiProductSelect: Laroute.route('franchise.vue.products'),
            products: [],
        };
    },

    computed: {
        canManageItems() {
            return Auth.hasAnyPermission(
                'franchise_manage_items',
                'franchise_manage_modifiers',
            );
        },

        modalId() {
            return 'modifier-modal';
        },

        ...mapGetters('modifierSet', [
            'modifierSet',
            'modes',
            'hasMultipleModifier',
            'totalPreSelected',
            'getPreselectedModifiers',
            'modifierExists',
        ]),

        disablePreselect() {
            const maxApply = this.modifierSet.max_apply_limit;
            return (
                this.totalPreSelected >= maxApply &&
                maxApply != 0 &&
                maxApply !== null
            );
        },

        modifiers: {
            get() {
                return this.modifierSet.modifiers;
            },
            set(value) {
                this.setModifiers(value);
            },
        },
    },

    watch: {
        // remove preselected as it should respect max limit
        'modifierSet.max_apply_limit'(newVal, oldVal) {
            if (
                this.totalPreSelected > newVal &&
                newVal != 0 &&
                newVal !== null
            ) {
                let totalShouldRemove = this.totalPreSelected - newVal;
                let preselectedModifiers = this.getPreselectedModifiers;

                preselectedModifiers
                    .sort((a, b) => {
                        if (a.original_index > b.original_index) {
                            return -1;
                        }

                        if (a.original_index < b.original_index) {
                            return 1;
                        }

                        return 0;
                    })
                    .forEach((m, index) => {
                        if (index >= totalShouldRemove) {
                            return;
                        }

                        let input = {
                            attribute: 'modifiers',
                            value: false,
                            modifier_attr: 'pre_selected',
                            modifier_index: m.original_index,
                        };

                        this.setModifierSetAttribute(input);
                    });
            }
        },
    },

    created() {
        this.resetModifierSet();
        this.registerEventBus();
    },

    mounted() {
        this.loadProducts(null);
    },

    methods: {
        ...mapActions('modifierSet', [
            'setModifierSetAttribute',
            'addModifier',
            'removeModifier',
            'createModifierSet',
            'updateModifierSet',
            'addModifierSet',
            'resetModifierSet',
            'getModifierSet',
            'setModifiers',
        ]),

        loadProducts: _.debounce(function(query) {
            let options = {
                params: {
                    query: query,
                    limit: 30,
                },
            };

            this.ajaxProductLoading = true;
            this.$http.get(this.apiProductSelect, options).then(response => {
                this.products = response.data.data;
                this.ajaxProductLoading = false;
            });
        }, 500),

        submit() {
            if (!this.validateForm('modifierSetForm')) return;

            const func = this.modifierExists
                ? 'updateModifierSet'
                : 'createModifierSet';

            this[func]()
                .then(res => {
                    if (!this.modifierExists) {
                        this.resetModifierSet();
                    } else {
                        this.hideModal();
                    }

                    this.showToastrSuccess();
                    this.$emit('modifierset-saved');
                })
                .catch(err => {
                    this.showApiError(err);
                });
        },

        showModal() {
            $(`#${this.modalId}`).modal('show');
        },

        hideModal() {
            $(`#${this.modalId}`).modal('hide');
        },

        updateModiferSet(
            attribute,
            event,
            modifier_attr = null,
            modifier_index = null,
        ) {
            let value;

            if (event.srcElement.type === 'checkbox') {
                value = event.target.checked;
            } else {
                value = event.target.value;
            }

            let input = {
                attribute,
                value,
                modifier_attr,
                modifier_index,
            };

            this.setModifierSetAttribute(input);
        },

        registerEventBus() {
            this.$on('show-form', modifierSet => {
                if (modifierSet === null) {
                    this.resetModifierSet();
                    this.showModal();
                } else {
                    this.getModifierSet(modifierSet.id)
                        .then(res => {
                            this.showModal();
                        })
                        .catch(err => this.showApiError(err));
                }
            });
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/inventory/modifier/vuetablemodifier_form.vue